import * as React from "react";
import { ArrowRightLeft, FileText, Info, Pencil, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/Combobox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Dropzone } from "@/components/ui/dropzone";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { PHONE_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { CURRENCIES } from "@/lib/countries";
import { buildFxLedger, fetchLedgerFxRates, FxLedgerRow, pairKey } from "@/lib/fx";
import { NetPayableLine } from "@/lib/settlementAmount";
import { DetailCard } from "@/components/requests/DetailCard";
import {
  ApiError,
  fxOverrideReceiptUrl,
  removeFxOverride,
  setFxOverride,
  setSettlementTarget
} from "../../api";
import { AdvanceAmount, ClaimMethod, ExpenseLineItem, FxOverride, TravelRequestSummary } from "../../types";
import { formatDate } from "@/lib/datetime";

function money(currency: string, amount: number) {
  const sign = amount < 0 ? "-" : "";
  return `${sign}${currency} ${Math.abs(amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** What a row's sameCurrOffset (actualExpense − advanceIssued) means - a
 *  positive offset is a real amount Finance owes the employee ("Net
 *  payable") and a negative one is owed back. Shared by both claim methods
 *  (confirmed desired behaviour: a Fixed Allowance employee is paid the
 *  allowance AND reimbursed for what they actually spent, same as
 *  Reimbursement's bills ARE the claim - there's no more separate
 *  "tracking-only, doesn't affect payout" framing for Fixed Allowance's
 *  bills). */
function offsetMeaning(offset: number): string {
  return offset > 0 ? "Net payable to the employee" : "Owed by the employee";
}

/** A rate for display. A fixed 4 decimals turned a weak currency's rate
 *  (1 IRR = 0.00000077511 AUD) into "0.0000", so a rate below 1 keeps 4
 *  significant digits instead. */
function formatRate(rate: number) {
  return rate >= 1
    ? rate.toLocaleString(undefined, { maximumFractionDigits: 4 })
    : rate.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}

/** A rate as a plain decimal for the override input - String(rate) gives
 *  scientific notation ("7.7511e-7") for very small rates. */
function rateInputText(rate: number) {
  return rate.toLocaleString("en-US", { useGrouping: false, maximumSignificantDigits: 10 });
}

// Stable (module-level, never re-created) fallbacks for when a caller omits
// advanceAmounts/fxOverrides - a `= []` DEFAULT PARAMETER instead would
// evaluate a fresh array on every render whenever the prop is undefined,
// which fed currencies (useMemo) -> targetCurrencyByCurrency (useMemo) ->
// the fetch useEffect's deps below, re-firing that effect (and its
// setLoading/setRates) on literally every render - an infinite fetch loop,
// confirmed live (hundreds of identical GET /api/fx/rate calls/sec, the
// card stuck on "Fetching exchange rates...") for any request with bills
// but no linked advance, where the caller passes advanceAmounts={undefined}.
const EMPTY_ADVANCE_AMOUNTS: AdvanceAmount[] = [];
const EMPTY_FX_OVERRIDES: FxOverride[] = [];
const EMPTY_SETTLEMENT_TARGETS: Record<string, string> = {};

/** "Override FX Rate" modal - matches the reference design's form: the
 *  fetched/standard rate shown read-only for comparison, a required custom
 *  rate field, an optional receipt upload, and a reason. Saved immediately
 *  on Apply (see requests_api.py's set_fx_override) - visible to anyone else
 *  who opens this request's ledger from that point on, and audited. */
function OverrideFxDialog({
  open,
  onOpenChange,
  travelRequestId,
  row,
  onSaved
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  travelRequestId: string;
  row: FxLedgerRow | null;
  onSaved: (updated: TravelRequestSummary) => void;
}) {
  const [rateText, setRateText] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [file, setFile] = React.useState<File | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open && row) {
      setRateText(row.savedOverride ? rateInputText(row.savedOverride.rate) : row.rate !== null ? rateInputText(row.rate) : "");
      setReason(row.savedOverride?.reason || "");
      setFile(null);
      setError(null);
    }
  }, [open, row]);

  if (!row) return null;
  const parsedRate = Number(rateText);
  const valid = rateText.trim() !== "" && Number.isFinite(parsedRate) && parsedRate > 0;
  // The standard rate to show for comparison - the fetched Frankfurter rate,
  // never a previously-set override (which is exactly what this form is
  // about to replace, so showing it as "standard" would be circular).
  const standardRate = row.savedOverride || row.isPendingOverride ? null : row.rate;

  const apply = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await setFxOverride(travelRequestId, {
        currency: row.currency,
        targetCurrency: row.targetCurrency,
        rate: parsedRate,
        reason: reason.trim() || undefined,
        file: file || undefined
      });
      toast.success("FX rate override applied");
      onOpenChange(false);
      onSaved(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save this override. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-primary" />
            Override FX Rate & Supporting Document
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Portfolio: {row.currency} → Target: {row.targetCurrency}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Standard Market Rate</Label>
            <div className="flex h-9 items-center rounded-md border bg-muted/50 px-3 text-sm text-muted-foreground">
              {standardRate !== null
                ? `1 ${row.currency} = ${formatRate(standardRate)} ${row.targetCurrency}`
                : "Not available"}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="override-rate" className="text-xs font-medium text-primary">
              Custom Override Rate *
            </Label>
            <Input
              id="override-rate"
              type="number"
              min={0}
              step="any"
              inputMode="decimal"
              value={rateText}
              onChange={(e) => setRateText(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="e.g. 1.4"
              autoFocus
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm">Upload Credit Card Bill / Money Exchange Receipt</Label>
          <Dropzone
            accept="image/*,.pdf"
            multiple={false}
            onFilesSelected={(files) => setFile(files[0] ?? null)}
          >
            {file ? (
              <>
                <FileText className="h-6 w-6 text-primary" />
                <p className="break-all text-sm font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground">Click to choose a different file</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">Click or drag &amp; drop exchange proof</p>
                <p className="text-xs text-muted-foreground">
                  Supports PDF, PNG, JPG (Credit card statement, receipts)
                </p>
              </>
            )}
          </Dropzone>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="override-reason" className="text-sm">
            Reason for Rate Override / Exemption
          </Label>
          <Textarea
            id="override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Applied actual bank exchange rate on credit card statement"
            rows={3}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={apply} disabled={!valid || saving}>
            {saving ? "Saving…" : "Apply FX Rate & Attach Proof"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The "how was this calculated" info icon on the Net payable tile - opens
 *  on hover on desktop (a mouse pointer genuinely hovers; controlled via
 *  open/onOpenChange rather than Radix's own default click/focus trigger,
 *  since the default has no hover behaviour at all) and on tap on phone
 *  (hover doesn't exist there - same reasoning the "Multiple rates"/
 *  "Same-curr offset" popovers elsewhere in this file were switched from a
 *  hover-only Tooltip to a tap-driven Popover for). Click still toggles it
 *  either way, so a desktop user who prefers clicking isn't left out. */
function InfoPopover({ lines }: { lines: NetPayableLine[] }) {
  const isPhone = useMediaQuery(PHONE_QUERY);
  const [open, setOpen] = React.useState(false);
  if (lines.length === 0) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="normal-case text-muted-foreground hover:text-foreground"
          aria-label="How this was calculated"
          onMouseEnter={() => !isPhone && setOpen(true)}
          onMouseLeave={() => !isPhone && setOpen(false)}
          onClick={() => setOpen((o) => !o)}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-xs" onMouseEnter={() => !isPhone && setOpen(true)} onMouseLeave={() => !isPhone && setOpen(false)}>
        <div className="space-y-1.5 text-left text-xs">
          {lines.map((line, i) => (
            <p key={i}>
              <span className="text-muted-foreground">{line.label}: </span>
              <span className="font-medium">{line.value}</span>
            </p>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** One ledger row's rate cell - a plain summary of the effective rate (saved
 *  override > pending override > fetched), with an "Override FX" button that
 *  opens OverrideFxDialog, and a way to clear a saved override. In readOnly
 *  mode the edit controls (Override FX / Reset) are hidden - the rate, any
 *  "Custom override" badge, reason and proof link are still shown since
 *  they're information about the request, not an edit affordance. */
function RateCell({
  row,
  travelRequestId,
  onOverrideClick,
  onClearSaved,
  clearing,
  readOnly
}: {
  row: FxLedgerRow;
  travelRequestId?: string;
  onOverrideClick: () => void;
  onClearSaved: () => void;
  clearing: boolean;
  readOnly?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs">
        {row.multipleRates ? (
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className="text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground">
                Multiple rates ({row.rateBreakdown.length})
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto max-w-xs">
              <div className="space-y-1 text-left text-xs">
                {row.rateBreakdown.map((b, i) => (
                  <p key={i}>
                    {b.date ? formatDate(b.date) : "Advance"}: 1 {row.currency} = {formatRate(b.rate)} {row.targetCurrency}
                    {" "}({money(row.currency, b.amount)})
                  </p>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        ) : row.rate === null ? (
          <span className="text-muted-foreground">No rate available</span>
        ) : (
          <>
            <span className="text-muted-foreground">
              1 {row.currency} = {formatRate(row.rate)} {row.targetCurrency}
            </span>
            {row.savedOverride && (
              <span className="ml-1.5 inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[0.625rem] font-medium text-amber-700 dark:text-amber-400">
                Custom override
              </span>
            )}
          </>
        )}
      </div>
      {row.savedOverride?.reason && (
        <p className="max-w-[16rem] truncate text-[0.6875rem] text-muted-foreground" title={row.savedOverride.reason}>
          {row.savedOverride.reason}
        </p>
      )}
      {row.savedOverride?.receiptBlobPath && travelRequestId && (
        <a
          href={fxOverrideReceiptUrl(travelRequestId, row.savedOverride.id)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-[0.6875rem] text-primary underline underline-offset-2"
        >
          <FileText className="h-3 w-3" /> View proof
        </a>
      )}
      {!readOnly && (
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 gap-1 px-2 text-[0.6875rem]"
            onClick={onOverrideClick}
          >
            <Pencil className="h-3 w-3" /> Override FX
          </Button>
          {row.savedOverride && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-[0.6875rem] text-muted-foreground"
              onClick={onClearSaved}
              disabled={clearing}
              title="Remove this override"
            >
              <RotateCcw className="h-3 w-3" /> {clearing ? "Removing…" : "Reset"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** The Valuation column's own value - row.valuationInTarget, converted
 *  straight from sameCurrOffset (actualExpense - advanceIssued, see
 *  lib/fx.ts). Shared by both claim methods (confirmed desired behaviour -
 *  see offsetMeaning's own doc comment on why Fixed Allowance no longer
 *  gets a separate framing from Reimbursement here). null stays null -
 *  nothing to show when there's no rate at all. */
function valuationForDisplay(row: FxLedgerRow): number | null {
  return row.valuationInTarget;
}

/** The "Actual expense"/"Actual expense with allowance" cell content -
 *  shared by the desktop table and phone card below. Plain money for an
 *  ordinary row; for Fixed Allowance's defaultTargetCurrency row (the one
 *  the synthetic allowance line was folded into - see ledgerExpenses), shows
 *  the combined total with an info popover breaking down the arithmetic
 *  ("Allowance: ALL 8,523" / "Bills: ALL 520" / "= ALL 9,043") rather than
 *  cramming it into the cell's own text - same tap/hover Popover pattern
 *  OffsetCell's info icon already uses in this file (confirmed desired -
 *  "show the math", "make that a popover"). */
function ExpenseCell({
  row,
  isAllowanceRow,
  allowanceAmount
}: {
  row: FxLedgerRow;
  isAllowanceRow: boolean;
  allowanceAmount?: number;
}) {
  if (row.actualExpense === 0) return <>-</>;
  const bills = isAllowanceRow && allowanceAmount !== undefined ? row.actualExpense - allowanceAmount : 0;
  if (!isAllowanceRow || allowanceAmount === undefined || bills === 0) {
    return <>{money(row.currency, row.actualExpense)}</>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      {money(row.currency, row.actualExpense)}
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="How this was calculated">
            <Info className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto px-3 py-1.5 text-xs">
          <div className="space-y-1 text-left">
            <p>
              <span className="text-muted-foreground">Allowance: </span>
              <span className="font-medium">{money(row.currency, allowanceAmount)}</span>
            </p>
            <p>
              <span className="text-muted-foreground">Bills: </span>
              <span className="font-medium">{money(row.currency, bills)}</span>
            </p>
            <p>
              <span className="text-muted-foreground">= </span>
              <span className="font-medium">{money(row.currency, row.actualExpense)}</span>
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </span>
  );
}

/** The "Same-curr offset" cell content - shared verbatim by both claim
 *  methods, the desktop table and the phone card layout below so none of
 *  them can drift apart (confirmed desired behaviour - see offsetMeaning's
 *  own doc comment). */
function OffsetCell({ row }: { row: FxLedgerRow }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={
          row.sameCurrOffset > 0
            ? "text-primary"
            : row.sameCurrOffset < 0
              ? "text-destructive"
              : "text-muted-foreground"
        }
      >
        {money(row.currency, row.sameCurrOffset)}
      </span>
      {row.sameCurrOffset !== 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              aria-label={offsetMeaning(row.sameCurrOffset)}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto px-3 py-1.5 text-xs">
            {offsetMeaning(row.sameCurrOffset)}
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}

/** Multi-currency reconciliation ledger: nets the linked advance against
 *  bills PER CURRENCY first (Same-Curr Offset), then converts only the
 *  leftover into a settlement currency for a net payment - using the
 *  Frankfurter API (lib/fx.ts, fx.py) with each bill's own transaction date.
 *  Each row can independently target a different settlement currency (a
 *  global picker just sets every row's starting point); anyone viewing can
 *  override a row's rate with their own, with an optional receipt as proof -
 *  saved immediately and visible to everyone else who opens this request.
 *  Always renders once there's at least one bill, even with a single
 *  currency and no advance - that row still shows the bill total at a flat
 *  1 rate, so every request has one place its reconciliation is shown.
 *

 *  Editable (per-row settlement currency, Override FX) in the wizard's
 *  Review step (still the employee's own draft) and SettleRequestDialog
 *  (Finance actually settling) - shown purely informationally there
 *  alongside Settled amount (see that component's own doc comment on why
 *  the two never feed each other). RequestDetailDialog - the plain "view
 *  this request" sheet used from My Reimbursements, My Approvals and the
 *  Approver Queue, never itself the approve/settle action - passes
 *  readOnly so nobody can change a settlement currency or set an FX
 *  override just by opening a request to look at it. */
export function CurrencyConversionCard({
  travelRequestId,
  expenses,
  advanceAmounts = EMPTY_ADVANCE_AMOUNTS,
  fxOverrides = EMPTY_FX_OVERRIDES,
  settlementTargetByCurrency: savedTargetByCurrency = EMPTY_SETTLEMENT_TARGETS,
  defaultTargetCurrency,
  departureDate,
  onOverridesChanged,
  readOnly = false,
  claimMethod = "reimbursement",
  estimatedAllowanceAmount
}: {
  /** Needed to call the override routes - omitted only when there's no real
   *  request yet to attach an override to (shouldn't occur in practice: a
   *  Reimbursement Draft exists by the time bills can be uploaded at all). */
  travelRequestId?: string;
  expenses: ExpenseLineItem[];
  /** The linked advance's per-currency amounts, if any - omit when there's
   *  no linked advance (every row then nets to just -actualExpense). */
  advanceAmounts?: AdvanceAmount[];
  /** Saved overrides already on the request (see types.ts's FxOverride) -
   *  the source of truth once set, taking priority over any fetched rate. */
  fxOverrides?: FxOverride[];
  /** Saved per-row settlement currency choices already on the request (see
   *  types.ts's TravelRequestSummary.settlementTargetByCurrency) - without
   *  this, a row's chosen target lived only in this component's own state
   *  and was lost the moment it unmounted (confirmed live: picked before
   *  submit, reverted to the request's own currency on reopen - "falls
   *  back to destination only"). */
  settlementTargetByCurrency?: Record<string, string>;
  defaultTargetCurrency: string;
  /** The trip's own departure date (detail.departureAt, or form.departureAt
   *  in the wizard before the request is submitted) - the advance's OWN
   *  conversion date, since AdvanceAmount itself carries no date (an
   *  advance is one lump sum, not dated per line - see lib/fx.ts's
   *  fxLedgerKeys). Each BILL still converts at its own transaction date
   *  regardless of this value; this only decides the advance side. */
  departureDate?: string | null;
  /** Fired with the fresh TravelRequestSummary after an override is saved or
   *  removed - both routes already return the whole updated request (see
   *  requests_api.py's set_fx_override/remove_fx_override), so the caller
   *  can apply it directly instead of doing a separate re-fetch. Omitted
   *  only where there's genuinely nothing to update with it (there always
   *  is in practice - every caller holds either the full request or state
   *  derived from it). */
  onOverridesChanged?: (updated: TravelRequestSummary) => void;
  /** True for a plain "view this request" surface where no editing of any
   *  kind should happen - hides the settlement-currency pickers and the
   *  Override FX / Reset buttons, leaving the ledger a pure read-out. */
  readOnly?: boolean;
  /** Both claim methods now frame the "Same-curr offset" column and bottom
   *  tile(s) identically: each currency's net (actualExpense −
   *  advanceIssued) is a real amount Finance settles, payable to/owed by the
   *  employee, with the same payable/owed colour coding either way
   *  (confirmed desired behaviour - a Fixed Allowance employee is paid the
   *  allowance AND reimbursed for what they actually spent, so the bills
   *  are no longer tracking-only; see offsetMeaning's own doc comment).
   *  Fixed Allowance additionally folds its estimated allowance into the
   *  final "Net payable" tile below (see estimatedAllowanceAmount), which
   *  Reimbursement has no equivalent of (its bills ARE the whole claim, with
   *  nothing else to add). */
  claimMethod?: ClaimMethod;
  /** Fixed Allowance only: the trip's estimated allowance, in
   *  defaultTargetCurrency - when given, an extra "Net payable" tile renders,
   *  equal to the allowance PLUS actual spend (every bill's own currency
   *  converted) LESS the FULL advance received - confirmed desired
   *  behaviour: the allowance and the bills are both paid out, not just one
   *  or the other. Shown in whichever currency every advance row above
   *  already agrees on as its settlement currency (so picking a currency for
   *  every row there is reflected here too), converting the allowance itself
   *  into that currency first - falling back to defaultTargetCurrency when
   *  the rows DON'T all agree on one (see this component's own render below
   *  for why a split can't just pick one of them). Omitted (the default) hides
   *  the tile entirely - every other caller of this component either isn't
   *  Fixed Allowance or has nothing meaningful to net the ledger against
   *  here (e.g. RequestDetailDialog already shows its own separate
   *  "Estimated allowance" figure elsewhere). */
  estimatedAllowanceAmount?: number;
}) {
  const isPhone = useMediaQuery(PHONE_QUERY);
  // Fixed Allowance's row for defaultTargetCurrency must always appear once
  // there's an allowance to fold in, even when every bill is in some OTHER
  // currency (e.g. an AFN-only bill with an ALL allowance) - otherwise the
  // allowance has no row of its own to show up in.
  const hasAllowance = claimMethod !== "reimbursement" && !!estimatedAllowanceAmount;
  const currencies = React.useMemo(
    () =>
      Array.from(
        new Set([
          ...expenses.map((e) => e.currency || defaultTargetCurrency),
          ...advanceAmounts.map((a) => a.currency),
          ...(hasAllowance ? [defaultTargetCurrency] : [])
        ])
      ).sort(),
    [expenses, advanceAmounts, defaultTargetCurrency, hasAllowance]
  );
  const hasAnyCurrency = currencies.length > 0;

  // Per-row settlement currency - defaults to defaultTargetCurrency for
  // every currency, independently changeable (see the per-row Combobox
  // below), PERSISTED immediately via setSettlementTarget so it survives a
  // reload instead of living only in this component's own state (see
  // settlementTargetByCurrency's own doc comment on the prop above).
  // Lazily seeded from the saved prop ONCE on mount - not kept in sync with
  // it afterward, since every actual change goes through setTargetFor/
  // applyGlobalTarget below, which update both this state and the saved
  // prop's source (the request document) together; re-deriving from the
  // prop on every change would just be this same state, one render later.
  const [targetByCurrency, setTargetByCurrency] = React.useState<Map<string, string>>(
    () => new Map(Object.entries(savedTargetByCurrency))
  );
  const targetFor = (currency: string) => targetByCurrency.get(currency) ?? defaultTargetCurrency;
  const [savingTarget, setSavingTarget] = React.useState<string | null>(null);
  const setTargetFor = async (currency: string, target: string) => {
    setTargetByCurrency((prev) => new Map(prev).set(currency, target));
    if (!travelRequestId || readOnly) return;
    setSavingTarget(currency);
    try {
      const updated = await setSettlementTarget(travelRequestId, currency, target);
      onOverridesChanged?.(updated);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save this settlement currency.");
    } finally {
      setSavingTarget(null);
    }
  };
  const [globalTarget, setGlobalTarget] = React.useState(defaultTargetCurrency);
  const applyGlobalTarget = async (target: string) => {
    setGlobalTarget(target);
    setTargetByCurrency(new Map(currencies.map((c) => [c, target])));
    // A session-local override was calibrated against the OLD target - it
    // no longer means anything once every row repoints at a new one.
    setPendingOverrides(new Map());
    if (!travelRequestId || readOnly) return;
    // defaultTargetCurrency (the request's own currency) is ALWAYS saved
    // too, even when it isn't one of `currencies` (no bill/advance uses it -
    // the Fixed Allowance "nothing to reconcile yet" case, see
    // hasAnyCurrency above) - without this, picking a currency here with no
    // bill/advance at all had nothing to save it under, so it was silently
    // lost the moment the component unmounted and the Settlement amount
    // table column (lib/settlementAmount.ts, which also keys off
    // defaultTargetCurrency for the allowance itself) never saw it at all
    // (confirmed live: picking BOB with 0 bills/0 advance showed "Net
    // payable (BOB)" here, but the table kept showing the request's own IRR
    // 30,000 - nothing had ever actually been persisted). Deduplicated via
    // the Set below so a currency that's both the request's own AND an
    // advance/bill currency isn't saved twice.
    const toSave = new Set([defaultTargetCurrency, ...currencies]);
    // One save per currency, one at a time: each is a read-modify-write of
    // the same document, so running them in parallel let them overwrite
    // each other and only the last row's choice survived.
    try {
      let latest: TravelRequestSummary | null = null;
      for (const c of toSave) {
        latest = await setSettlementTarget(travelRequestId, c, target);
      }
      if (latest) onOverridesChanged?.(latest);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save the settlement currency for every row.");
    }
  };

  type RateInfo = { rate: number | null; asOfDate: string | null; usedFallbackRate: boolean };
  const [rates, setRates] = React.useState<Map<string, RateInfo>>(new Map());
  // Mirrors `rates` for the fetch effect below to read synchronously
  // (what's already fetched, so it only requests what's missing) WITHOUT
  // depending on `rates` itself in that effect's own dependency array -
  // `rates` changes as a RESULT of this effect's own setRates call, so
  // depending on it would re-fire the effect every time it just updated
  // the very state it reads, recreating the exact infinite-fetch-loop
  // class of bug this file already had once (see EMPTY_ADVANCE_AMOUNTS's
  // own comment above for that incident).
  const ratesRef = React.useRef(rates);
  ratesRef.current = rates;
  const [loading, setLoading] = React.useState(false);
  // Session-local (not yet saved) override, keyed by "currency->target" -
  // superseded instantly once the same pair gets a saved override.
  const [pendingOverrides, setPendingOverrides] = React.useState<Map<string, number>>(new Map());

  const targetCurrencyByCurrency = React.useMemo(
    () => new Map(currencies.map((c) => [c, targetFor(c)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencies, targetByCurrency, defaultTargetCurrency]
  );
  // Whether any row actually needs converting - NOT just "more than one
  // currency present": a single-currency request still needs a fetch the
  // moment its one row is pointed at a different settlement currency (the
  // "Set all to…" picker, or a per-row Combobox). Gating this on the raw
  // currency count instead used to leave that case stuck at the flat
  // same-currency rate forever - confirmed live, changing the settlement
  // currency on a single-currency request never issued a single
  // GET /api/fx/rate call, so the ledger kept showing the original
  // currency's rate=1 row relabelled into a target it never actually
  // converted into.
  const needsConversion = [...targetCurrencyByCurrency].some(([cur, target]) => cur !== target);

  // Fixed Allowance: fold the allowance into the ledger as a synthetic bill
  // in the request's own currency, so the table's "Actual expense"/
  // "Same-curr offset"/"Valuation" cells for that currency read allowance +
  // real bills (e.g. ALL 8,523 allowance + ALL 520 of bills = ALL 9,043),
  // matching the "Net payable" tile below rather than showing just the
  // bills and leaving the allowance to appear only in that separate tile.
  // Same trick notifications.py/reports/request_report.py use server-side.
  // Reimbursement has no allowance to fold in, so its ledger is unchanged.
  const ledgerExpenses = React.useMemo(() => {
    if (hasAllowance) {
      return [
        ...expenses,
        {
          id: "__allowance__",
          category: "Allowance",
          amount: estimatedAllowanceAmount,
          currency: defaultTargetCurrency,
          vendor: null,
          description: null,
          date: departureDate ?? null,
          blobPath: null,
          filename: null,
          contentType: null,
          uploadedAt: departureDate ?? ""
        } as ExpenseLineItem
      ];
    }
    return expenses;
  }, [expenses, hasAllowance, estimatedAllowanceAmount, defaultTargetCurrency, departureDate]);

  React.useEffect(() => {
    if (!needsConversion) return;
    let cancelled = false;
    setLoading(true);
    // alreadyHave: only request rates genuinely missing from what's
    // already fetched - changing ONE row's settlement currency used to
    // refetch EVERY row's rate (fetchLedgerFxRates always fetched the
    // whole key set, and this effect re-fires on any change to
    // targetCurrencyByCurrency, a single Map covering every currency -
    // confirmed live). Merging onto the previous state (the setRates
    // functional form) instead of replacing it wholesale means an
    // unchanged row's rate just stays as it was, with no refetch at all.
    fetchLedgerFxRates(ledgerExpenses, advanceAmounts, defaultTargetCurrency, targetCurrencyByCurrency, departureDate, ratesRef.current)
      .then((result) => !cancelled && setRates((prev) => new Map([...prev, ...result])))
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerExpenses, advanceAmounts, defaultTargetCurrency, targetCurrencyByCurrency, departureDate, needsConversion]);

  const ledger = React.useMemo(
    () =>
      buildFxLedger(
        ledgerExpenses, advanceAmounts, defaultTargetCurrency, defaultTargetCurrency,
        targetCurrencyByCurrency, rates, pendingOverrides, fxOverrides, departureDate
      ),
    [ledgerExpenses, advanceAmounts, defaultTargetCurrency, targetCurrencyByCurrency, rates, pendingOverrides, fxOverrides, departureDate]
  );

  // Breakdown popover for a "Net payable (<targetCurrency>)" tile below -
  // one line per ledger row that actually fed into THAT tile's number (every
  // row whose own targetCurrency matches, same grouping buildFxLedger's own
  // netPaymentByCurrency uses - see lib/fx.ts), not just the
  // defaultTargetCurrency row. Confirmed live bug this fixes: a request with
  // INR, SGD and USD rows ALL pointed at INR correctly collapsed into one
  // "Net payable (INR)" tile, but the popover only ever explained the INR
  // row's own allowance+bills-advance, silently leaving out what the SGD and
  // USD rows each converted to and contributed - "why showing only one
  // calculation."
  //
  // Each row's own valuationInTarget (sameCurrOffset = actualExpense -
  // advanceIssued, already converted) is exactly what buildFxLedger itself
  // sums into this tile's number - reused as-is rather than recomputing the
  // conversion a second time here, so this can never disagree with the tile.
  // The allowance (never a row of its own - see ledgerExpenses above, which
  // folds it into the defaultTargetCurrency row's actualExpense) gets one
  // extra line on top, and that row's own line is then just its BILLS
  // (actualExpense minus the allowance), not its full offset, so the
  // allowance isn't silently counted twice. A row with no rate available
  // (valuationInTarget null) is skipped - same "leave it out" rule the tile
  // itself follows via unconvertedOffsets. */
  const netPayableBreakdown = (targetCurrency: string): NetPayableLine[] => {
    const rows = ledger.rows.filter((r) => r.targetCurrency === targetCurrency && r.valuationInTarget !== null);
    if (rows.length === 0) return [];
    const lines: NetPayableLine[] = [];
    if (hasAllowance && targetCurrency === defaultTargetCurrency) {
      lines.push({ label: "Allowance", value: money(defaultTargetCurrency, estimatedAllowanceAmount!) });
    }
    let total = 0;
    for (const row of rows) {
      const isAllowanceRow = hasAllowance && row.currency === defaultTargetCurrency;
      const label = row.currency === targetCurrency ? "Same-currency offset" : `Offset (${row.currency})`;
      if (isAllowanceRow) {
        // This row's valuationInTarget already includes the allowance (it's
        // in actualExpense) - the Allowance line above already covers that
        // part, so only the bills-minus-advance remainder needs its own
        // line here, to avoid double-counting the allowance in the sum.
        const billsOffset = row.valuationInTarget! - estimatedAllowanceAmount!;
        if (billsOffset !== 0) lines.push({ label, value: money(targetCurrency, billsOffset) });
        total += row.valuationInTarget!;
      } else {
        if (row.valuationInTarget !== 0) lines.push({ label, value: money(targetCurrency, row.valuationInTarget!) });
        total += row.valuationInTarget!;
      }
    }
    lines.push({
      label: total >= 0 ? "Net payable" : "Amount owed back",
      value: money(targetCurrency, total)
    });
    return lines;
  };

  const [overrideTarget, setOverrideTarget] = React.useState<FxLedgerRow | null>(null);
  const [clearingKey, setClearingKey] = React.useState<string | null>(null);

  const clearSavedOverride = async (row: FxLedgerRow) => {
    if (!travelRequestId || !row.savedOverride) return;
    const key = pairKey(row.currency, row.targetCurrency);
    setClearingKey(key);
    try {
      const updated = await removeFxOverride(travelRequestId, row.savedOverride.id);
      toast.success("FX override removed");
      onOverridesChanged?.(updated);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not remove this override.");
    } finally {
      setClearingKey(null);
    }
  };

  // Nothing to reconcile at all (no bills, no advance) - still worth
  // showing for Fixed Allowance, whose Net payable tile is just the
  // allowance itself in that case: the settlement-currency picker still has
  // a real job (choosing what currency the allowance pays out in) even with
  // no rows to list, so the card stays up for that reason alone rather than
  // disappearing entirely until an advance or bill eventually shows up.
  // Reimbursement has no such tile without a bill/advance to begin with, so
  // it keeps disappearing exactly as before.
  const showWithNothingToReconcile = claimMethod !== "reimbursement" && estimatedAllowanceAmount !== undefined;
  if (!hasAnyCurrency && !showWithNothingToReconcile) return null;

  // Fixed Allowance's defaultTargetCurrency row now includes the folded-in
  // allowance (see ledgerExpenses above), so its "Actual expense" column no
  // longer means just bills for that one row - renamed per the client's own
  // requirement (allowance + actual spend = one combined payout).
  const actualExpenseLabel = hasAllowance ? "Actual expense with allowance" : "Actual expense";

  return (
    <DetailCard icon={ArrowRightLeft} title="Multi-currency reconciliation">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          Reconciles the advance against bills in each currency, then
          converts the leftover into a settlement currency - each row can
          target a different currency.
        </p>
        {!readOnly && (
          <div className="w-full sm:w-40 sm:shrink-0">
            <Combobox
              value={globalTarget}
              onChange={applyGlobalTarget}
              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
              placeholder="Set all to…"
              searchPlaceholder="Search…"
            />
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Fetching exchange rates…</p>
      ) : (
        <div className="space-y-3">
          {!hasAnyCurrency ? (
            <p className="text-sm text-muted-foreground">
              No bills or advance to reconcile yet - pick the currency the
              allowance should pay out in below.
            </p>
          ) : isPhone ? (
            // Phone: one card per currency, label/value rows stacked instead
            // of table columns - the same 7 fields as the table below, just
            // laid out so nothing needs horizontal scrolling on a narrow
            // screen.
            <div className="space-y-3">
              {ledger.rows.map((row) => (
                <div key={row.currency} className="rounded-md border p-3 text-sm">
                  <p className="mb-2 font-medium">{row.currency}</p>
                  <dl className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-xs text-muted-foreground">Advance issued</dt>
                      <dd className="text-muted-foreground">
                        {row.advanceIssued > 0 ? money(row.currency, row.advanceIssued) : "-"}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-xs text-muted-foreground">{actualExpenseLabel}</dt>
                      <dd className="text-muted-foreground">
                        <ExpenseCell
                          row={row}
                          isAllowanceRow={hasAllowance && row.currency === defaultTargetCurrency}
                          allowanceAmount={estimatedAllowanceAmount}
                        />
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-xs text-muted-foreground">Same-curr offset</dt>
                      <dd>
                        <OffsetCell row={row} />
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-xs text-muted-foreground">Settlement currency</dt>
                      <dd>
                        {readOnly ? (
                          <span className="font-medium">{row.targetCurrency}</span>
                        ) : (
                          <div className="w-28">
                            <Combobox
                              value={row.targetCurrency}
                              onChange={(v) => setTargetFor(row.currency, v)}
                              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                              placeholder="Currency…"
                              searchPlaceholder="Search…"
                              disabled={savingTarget === row.currency}
                            />
                          </div>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="pt-0.5 text-xs text-muted-foreground">FX rate &amp; override</dt>
                      <dd className="text-right">
                        {row.currency === row.targetCurrency ? (
                          <span className="text-xs text-muted-foreground">-</span>
                        ) : (
                          <RateCell
                            row={row}
                            travelRequestId={travelRequestId}
                            onOverrideClick={() => setOverrideTarget(row)}
                            onClearSaved={() => clearSavedOverride(row)}
                            clearing={clearingKey === pairKey(row.currency, row.targetCurrency)}
                            readOnly={readOnly}
                          />
                        )}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t pt-2">
                      <dt className="text-xs font-medium">Valuation</dt>
                      <dd className="font-medium">
                        {(() => {
                          const value = valuationForDisplay(row);
                          return value === null ? (
                            <span className="text-xs text-muted-foreground">-</span>
                          ) : (
                            money(row.targetCurrency, value)
                          );
                        })()}
                      </dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto px-4">
              <table className="w-full min-w-[57.5rem] text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="py-1.5 pr-4 font-medium">Currency</th>
                    <th className="py-1.5 pr-4 font-medium">Advance issued</th>
                    <th className="py-1.5 pr-4 font-medium">{actualExpenseLabel}</th>
                    <th className="py-1.5 pr-4 font-medium">Same-curr offset</th>
                    <th className="py-1.5 pr-4 font-medium">Settlement currency</th>
                    <th className="py-1.5 pr-4 font-medium">FX rate &amp; override</th>
                    <th className="py-1.5 text-right font-medium">Valuation</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.rows.map((row) => (
                    <tr key={row.currency} className="border-t align-top">
                      <td className="py-2.5 pr-4 font-medium">{row.currency}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">
                        {row.advanceIssued > 0 ? money(row.currency, row.advanceIssued) : "-"}
                      </td>
                      <td className="py-2.5 pr-4 text-muted-foreground">
                        <ExpenseCell
                          row={row}
                          isAllowanceRow={hasAllowance && row.currency === defaultTargetCurrency}
                          allowanceAmount={estimatedAllowanceAmount}
                        />
                      </td>
                      <td className="py-2.5 pr-4">
                        <OffsetCell row={row} />
                      </td>
                      <td className="py-2.5 pr-4">
                        {readOnly ? (
                          <span className="font-medium">{row.targetCurrency}</span>
                        ) : (
                          <div className="w-28">
                            <Combobox
                              value={row.targetCurrency}
                              onChange={(v) => setTargetFor(row.currency, v)}
                              options={CURRENCIES.map((c) => ({ value: c, label: c }))}
                              placeholder="Currency…"
                              searchPlaceholder="Search…"
                              disabled={savingTarget === row.currency}
                            />
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 pr-4">
                        {row.currency === row.targetCurrency ? (
                          <span className="text-xs text-muted-foreground">-</span>
                        ) : (
                          <RateCell
                            row={row}
                            travelRequestId={travelRequestId}
                            onOverrideClick={() => setOverrideTarget(row)}
                            onClearSaved={() => clearSavedOverride(row)}
                            clearing={clearingKey === pairKey(row.currency, row.targetCurrency)}
                            readOnly={readOnly}
                          />
                        )}
                      </td>
                      <td className="py-2.5 text-right">
                        {(() => {
                          const value = valuationForDisplay(row);
                          return value === null ? (
                            <span className="text-xs text-muted-foreground">-</span>
                          ) : (
                            money(row.targetCurrency, value)
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {/* Per-currency offset tiles - identical framing for both claim
                methods now (confirmed desired behaviour: a Fixed Allowance
                employee is paid the allowance AND reimbursed for what they
                actually spent, so these bills count toward payable exactly
                like Reimbursement's own - see offsetMeaning's own doc
                comment). These are the ONLY "Net payable" figures shown -
                there is no separate, independently-computed pooled tile.
                Each tile's info popover (netPayableBreakdown) explains EVERY
                ledger row that was summed into it, not just one - a tile can
                combine several source currencies once they're all pointed at
                the same settlement currency (e.g. INR, SGD and USD rows all
                settling in INR collapse into one "Net payable (INR)" tile;
                the popover lists each one's own contribution). */}
            {ledger.netPayments.map((net) => (
              <div key={net.currency} className="min-w-[12rem] flex-1 rounded-md border bg-muted/40 p-3">
                <p className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {offsetMeaning(net.amount)} ({net.currency})
                  <InfoPopover lines={netPayableBreakdown(net.currency)} />
                </p>
                <p
                  className={
                    net.amount >= 0
                      ? "text-lg font-semibold text-primary"
                      : "text-lg font-semibold text-destructive"
                  }
                >
                  {money(net.currency, Math.abs(net.amount))}
                </p>
              </div>
            ))}
          </div>

          {ledger.unconvertedOffsets.length > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Some currencies couldn't be converted (no exchange rate is
              available) and are left out of the figures above - override
              their rate to include them.
            </p>
          )}
        </div>
      )}

      <OverrideFxDialog
        open={!!overrideTarget}
        onOpenChange={(open) => !open && setOverrideTarget(null)}
        travelRequestId={travelRequestId || ""}
        row={overrideTarget}
        onSaved={(updated) => onOverridesChanged?.(updated)}
      />
    </DetailCard>
  );
}
